import { useQuery } from "@tanstack/react-query";
import { ExternalLink, User } from "lucide-react";
import type { JSX, ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Sheet, SheetBody, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { formatTimestamp } from "@/lib/format";
import { type SourceFullResponse, getSourceFull } from "@/lib/sources";

interface SourceDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceId: string | null;
  /** Render at a wider max-width (used by the dock's expand-to-overlay). */
  wide?: boolean;
}

const SOURCE_TYPE_LABELS: Record<string, string> = {
  upload: "Upload",
  chat: "Chat",
  url: "URL",
  github: "GitHub",
  notion: "Notion",
  shortcut: "Shortcut",
  postman: "Postman",
  s3: "S3",
  self: "System",
  story_as_built: "Implementación real",
};

function formatDateTime(iso: string): string {
  return formatTimestamp(iso);
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-label-3 uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-body-3 font-medium">{value}</div>
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }): JSX.Element {
  return <h3 className="text-label-3 uppercase tracking-wide text-muted-foreground">{children}</h3>;
}

function SourceDetailPanel({ data }: { data: SourceFullResponse }): JSX.Element {
  const { item, chunks, entities } = data;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Badge variant="muted">{SOURCE_TYPE_LABELS[item.source_type] ?? item.source_type}</Badge>
          <Badge variant={item.status === "approved" ? "success" : "warning"}>{item.status}</Badge>
        </div>
        {item.description ? (
          <p className="text-body-3 text-muted-foreground">{item.description}</p>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Stat label="Formato" value={item.source_format ?? "—"} />
        <Stat label="Sync" value={item.sync_state} />
      </div>

      <div className="space-y-1">
        <SectionTitle>Autor</SectionTitle>
        <div className="flex items-center gap-2 text-body-3">
          <User size={14} className="text-muted-foreground" aria-hidden />
          <span>{item.submitted_by_email ?? "Usuario desconocido"}</span>
        </div>
      </div>

      <div className="space-y-1">
        <SectionTitle>Fechas</SectionTitle>
        <div className="flex justify-between text-body-3">
          <span className="text-muted-foreground">Creado</span>
          <span>{formatDateTime(item.created_at)}</span>
        </div>
        {item.last_synced_at ? (
          <div className="flex justify-between text-body-3">
            <span className="text-muted-foreground">Último sync</span>
            <span>{formatDateTime(item.last_synced_at)}</span>
          </div>
        ) : null}
      </div>

      {item.external_url ? (
        <div className="space-y-1">
          <SectionTitle>URL externa</SectionTitle>
          <a
            href={item.external_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-body-3 text-primary hover:underline"
          >
            <ExternalLink size={12} aria-hidden />
            Abrir fuente
          </a>
        </div>
      ) : null}

      {chunks.length > 0 ? (
        <div className="space-y-1">
          <SectionTitle>Chunks ({chunks.length})</SectionTitle>
          <details className="group">
            <summary className="cursor-pointer list-none text-label-3 text-muted-foreground hover:text-foreground">
              <span className="mr-1 inline-block transition-transform group-open:rotate-90">▸</span>
              Ver chunks
            </summary>
            <ul className="mt-2 space-y-2">
              {chunks.map((chunk) => (
                <li key={chunk.id} className="rounded border border-border p-2">
                  <p className="text-label-3 text-muted-foreground">Chunk #{chunk.ord}</p>
                  <p className="text-body-3 line-clamp-4">{chunk.text}</p>
                </li>
              ))}
            </ul>
          </details>
        </div>
      ) : null}

      {entities.length > 0 ? (
        <div className="space-y-1">
          <SectionTitle>Entidades ({entities.length})</SectionTitle>
          <div className="flex flex-wrap gap-1">
            {entities.map((entity) => (
              <Badge key={`${entity.type}-${entity.name}`} variant="info">
                {entity.name}
                <span className="text-label-3 opacity-60">({entity.type})</span>
              </Badge>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Source detail body (fetch + states + panel). Shared by the dock detail view
 * (>= xl) and the SourceDetailSheet lightbox (< xl / expanded). */
export function SourceDetailContent({ sourceId }: { sourceId: string | null }): JSX.Element {
  const query = useQuery({
    queryKey: ["sources", "full", sourceId],
    queryFn: () => getSourceFull(sourceId as string),
    enabled: sourceId !== null,
    staleTime: 60_000,
  });
  if (query.isError) {
    return <p className="text-body-3 text-destructive">No se pudo cargar el source.</p>;
  }
  if (query.isLoading || !query.data) {
    return <p className="text-body-3 text-muted-foreground">Cargando...</p>;
  }
  return (
    <div className="space-y-3">
      <h2 className="text-heading-3 font-semibold">{query.data.item.title}</h2>
      <SourceDetailPanel data={query.data} />
    </div>
  );
}

export function SourceDetailSheet({
  open,
  onOpenChange,
  sourceId,
  wide = false,
}: SourceDetailSheetProps): JSX.Element {
  const query = useQuery({
    queryKey: ["sources", "full", sourceId],
    queryFn: () => getSourceFull(sourceId as string),
    enabled: open && sourceId !== null,
    staleTime: 60_000,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange} ariaLabel="Detalle de fuente">
      <SheetContent
        side="right"
        onClose={() => onOpenChange(false)}
        className={wide ? "w-full sm:max-w-3xl" : "w-full sm:max-w-md"}
      >
        <SheetHeader>
          <SheetTitle>{query.data?.item.title ?? "Fuente"}</SheetTitle>
        </SheetHeader>
        <SheetBody>
          {query.isError ? (
            <p className="text-body-3 text-destructive">No se pudo cargar el source.</p>
          ) : query.isLoading || !query.data ? (
            <p className="text-body-3 text-muted-foreground">Cargando...</p>
          ) : (
            <SourceDetailPanel data={query.data} />
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
